
import React, { useState } from 'react';

interface Props {
  currentView: string;
  setView: (v: any) => void;
  onLogout: () => void;
  userName: string;
  credits: number;
  onAddCredits: () => void;
  onOpenFeedback: () => void;
  onNavigateToBlog?: () => void;
}

const Navigation: React.FC<Props> = ({ currentView, setView, onLogout, userName, credits, onAddCredits, onOpenFeedback, onNavigateToBlog }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg> },
    { id: 'blog', label: 'Blog', icon: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg> },
    { id: 'pinned', label: 'Revision', icon: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="m12 17 1-2V5c0-1.1-.9-2-2-2S9 3.9 9 5v10l1 2v5h2v-5Z"/></svg> },
    { id: 'insights', label: 'Analytics', icon: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="m19 20-4-4h3V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h8l4 4v-4Z"/></svg> },
  ];

  const handleNavClick = (view: any) => {
    if (view === 'blog' && onNavigateToBlog) {
      onNavigateToBlog();
    } else {
      setView(view);
    }
    setIsDrawerOpen(false);
  };

  return (
    <>
      <nav className="bg-white/80 backdrop-blur-md border-b border-slate-100 sticky top-0 z-50 print:hidden">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center h-16 sm:h-20">
            
            {/* Logo & Mobile Menu Toggle */}
            <div className="flex items-center gap-2 sm:gap-4">
              <button 
                onClick={() => setIsDrawerOpen(true)}
                className="lg:hidden p-2 -ml-2 text-slate-500 hover:text-slate-900 transition-colors"
                aria-label="Open Menu"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
              </button>

              <div className="flex items-center space-x-2 sm:space-x-3 cursor-pointer group" onClick={() => handleNavClick('dashboard')}>
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-xl sm:rounded-2xl flex items-center justify-center group-hover:rotate-6 transition-all shadow-lg shadow-slate-200 overflow-hidden">
                  <img src="/Logo1.png" alt="Logo" className="w-full h-full object-cover" />
                </div>
                <div className="flex flex-col">
                  <img src="/logo.png" alt="NHS Logo" className="h-8 sm:h-10 w-auto" />
                  <span className="hidden sm:block text-[9px] font-black text-blue-400 uppercase tracking-widest mt-1">Interview Academy</span>
                </div>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex space-x-8">
              {navItems.map((item) => (
                <button 
                  key={item.id}
                  onClick={() => setView(item.id as any)}
                  className={`text-[10px] font-black uppercase tracking-[0.2em] transition-all relative py-2 ${currentView === item.id ? 'text-blue-600' : 'text-slate-400 hover:text-slate-900'}`}
                >
                  {item.label}
                  {currentView === item.id && <span className="absolute bottom-0 left-0 w-full h-1 bg-blue-600 rounded-full"></span>}
                </button>
              ))}
              <button 
                onClick={onOpenFeedback}
                className="text-[10px] font-black text-amber-500 uppercase tracking-[0.2em] hover:text-amber-600 transition-all flex items-center gap-1.5"
              >
                Feedback
                <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse"></span>
              </button>
            </div>

            {/* Common Header Actions */}
            <div className="flex items-center gap-2 sm:gap-6">
              <div className="flex items-center gap-2 sm:gap-3 bg-slate-50 border border-slate-200 px-2 sm:px-4 py-1.5 rounded-xl sm:rounded-2xl">
                <div className="flex flex-col items-end">
                  <span className="hidden sm:block text-[8px] font-black text-slate-400 uppercase tracking-widest">Balance</span>
                  <span className={`text-[10px] sm:text-xs font-black ${credits < 10 ? 'text-red-500 animate-pulse' : 'text-slate-700'}`}>{credits}</span>
                </div>
                <button 
                  onClick={onAddCredits}
                  className="bg-blue-600 text-white p-1 sm:p-1.5 rounded-lg sm:rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-100 active:scale-90"
                  title="Add Credits"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 sm:w-[14px] sm:h-[14px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </button>
              </div>

              <div className="hidden md:block text-right">
                <p className="text-[9px] font-black text-slate-300 uppercase tracking-widest mb-0.5">Dr.</p>
                <p className="text-sm font-bold text-slate-800 line-clamp-1">{userName}</p>
              </div>

              <button
                onClick={onLogout}
                className="bg-red-600 text-white px-3 sm:px-7 py-2 sm:py-3 rounded-lg sm:rounded-2xl text-[10px] sm:text-[11px] font-black uppercase tracking-widest hover:bg-red-700 transition-all shadow-xl active:scale-95 flex items-center gap-2"
                title="Sign Out"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16,17 21,12 16,7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                Sign <span className="hidden sm:inline">Out</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Drawer Overlay */}
      {isDrawerOpen && (
        <div 
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[60] lg:hidden animate-in fade-in duration-300"
          onClick={() => setIsDrawerOpen(false)}
        />
      )}

      {/* Mobile Drawer */}
      <div className={`fixed top-0 left-0 bottom-0 w-[280px] bg-white z-[70] lg:hidden transform transition-transform duration-300 ease-out shadow-2xl flex flex-col ${isDrawerOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center overflow-hidden shadow-lg shadow-slate-200">
              <img src="/Logo1.png" alt="Logo" className="w-full h-full object-cover" />
            </div>
            <img src="/logo.png" alt="NHS Logo" className="h-6 w-auto" />
          </div>
          <button onClick={() => setIsDrawerOpen(false)} className="text-slate-400 p-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div className="flex-grow py-6 px-4 space-y-2">
          {navItems.map((item) => (
            <button 
              key={item.id}
              onClick={() => handleNavClick(item.id)}
              className={`w-full flex items-center gap-4 px-4 py-4 rounded-2xl text-sm font-black transition-all ${currentView === item.id ? 'bg-blue-50 text-blue-600' : 'text-slate-500 hover:bg-slate-50'}`}
            >
              <span className={currentView === item.id ? 'text-blue-600' : 'text-slate-400'}>{item.icon}</span>
              <span className="uppercase tracking-widest">{item.label}</span>
            </button>
          ))}
          
          <div className="pt-4 mt-4 border-t border-slate-50">
            <button 
              onClick={() => { onOpenFeedback(); setIsDrawerOpen(false); }}
              className="w-full flex items-center gap-4 px-4 py-4 rounded-2xl text-sm font-black text-amber-600 hover:bg-amber-50 transition-all"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              <span className="uppercase tracking-widest">Feedback</span>
            </button>
          </div>
        </div>

        <div className="p-6 border-t border-slate-100 space-y-4">
          <button 
            onClick={() => { onLogout(); setIsDrawerOpen(false); }}
            className="w-full py-3 rounded-xl border border-slate-200 text-xs font-black text-slate-400 uppercase tracking-widest hover:bg-red-50 hover:text-red-600 transition-all"
          >
            Sign Out
          </button>
        </div>
      </div>
    </>
  );
};

export default Navigation;
